import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { DollarSign, Users, TrendingUp, TrendingDown, BarChart3, RefreshCw, ArrowUpRight, ArrowDownRight, Mail, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  pro: '#10b981',
  business: '#3b82f6',
  business_plus: '#8b5cf6',
  'Back on Track': '#f59e0b',
  unknown: '#9ca3af',
};

const PIE_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6366f1'];

export const RevenueDashboard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/api/mybookie/admin/revenue-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const sendReport = async () => {
    setSendingReport(true);
    try {
      await axios.post(`${API}/api/mybookie/admin/revenue-report/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Revenue report sent to admin email!');
      fetchHistory();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/mybookie/admin/revenue-report/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportHistory(res.data.reports || []);
    } catch (err) {
      console.error('Failed to load report history:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchHistory();
  }, [fetchData, fetchHistory]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="revenue-dashboard-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((n) => (
            <Card key={`skeleton-${n}`}>
              <CardContent className="pt-6">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-3"></div>
                <div className="h-8 w-28 bg-gray-200 rounded animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Card><CardContent className="pt-6 h-64 bg-gray-50 animate-pulse rounded" /></Card>
          <Card><CardContent className="pt-6 h-64 bg-gray-50 animate-pulse rounded" /></Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6 text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <Button variant="outline" className="mt-4" onClick={fetchData}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const tierDistData = Object.entries(data.tier_distribution || {})
    .filter(([_, v]) => v > 0)
    .map(([tier, count]) => ({
      name: tier === 'business_plus' ? 'Business+' : tier.charAt(0).toUpperCase() + tier.slice(1),
      value: count,
    }));

  return (
    <div className="space-y-6" data-testid="revenue-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#111614]">Revenue Dashboard</h2>
          <p className="text-sm text-[#5C6661]">Real-time business metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={sendReport}
            disabled={sendingReport}
            data-testid="send-report-btn"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Mail className="w-4 h-4 mr-2" />
            {sendingReport ? 'Sending...' : 'Send Report Now'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            data-testid="toggle-history-btn"
          >
            <Clock className="w-4 h-4 mr-2" />
            History
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-revenue-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Automated Report Info */}
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
        <Mail className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-emerald-800">
            <span className="font-medium">Weekly email reports</span> are sent automatically every Monday at 8 AM UTC.
            {reportHistory.length > 0 && (
              <span className="text-emerald-600 ml-1">
                Last sent: {new Date(reportHistory[0].sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Report History */}
      {showHistory && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Report History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reportHistory.length === 0 ? (
              <p className="text-sm text-[#5C6661] text-center py-4">No reports sent yet. Click &quot;Send Report Now&quot; to send your first one.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {reportHistory.map((report) => (
                  <div key={report.sent_at} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="text-sm font-medium text-[#111614]">
                        {new Date(report.sent_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      <p className="text-xs text-[#5C6661]">
                        {new Date(report.sent_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-600">
                        MRR: ${report.snapshot?.mrr?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-[#5C6661]">
                        {report.snapshot?.total_users || 0} users
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">MRR</span>
            </div>
            <p className="text-2xl font-bold text-[#111614]" data-testid="mrr-value">
              ${data.mrr.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-[#5C6661] mt-1">
              ARR: ${data.arr.toLocaleString('en-US', { minimumFractionDigits: 0 })}
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-[#111614]" data-testid="total-revenue-value">
              ${data.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-[#5C6661] mt-1">
              {data.paying_users} paying user{data.paying_users !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${data.churn_rate > 10 ? 'border-l-red-500' : data.churn_rate > 5 ? 'border-l-amber-500' : 'border-l-emerald-500'}`}>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              {data.churn_rate > 5 ? <TrendingDown className="w-4 h-4 text-red-500" /> : <ArrowDownRight className="w-4 h-4 text-emerald-500" />}
              <span className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">Churn Rate</span>
            </div>
            <p className="text-2xl font-bold text-[#111614]" data-testid="churn-rate-value">
              {data.churn_rate}%
            </p>
            <p className="text-xs text-[#5C6661] mt-1">
              {data.churned_last_30d} churned (30d)
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-violet-600" />
              <span className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">Users</span>
            </div>
            <p className="text-2xl font-bold text-[#111614]" data-testid="total-users-value">
              {data.total_users}
            </p>
            <p className="text-xs text-[#5C6661] mt-1">
              {data.paying_users} paid / {data.total_users - data.paying_users} free
            </p>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards Row 2 */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <span className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">ARPU</span>
            <p className="text-xl font-bold text-[#111614] mt-1">${data.arpu}</p>
            <p className="text-xs text-[#5C6661]">Avg Revenue Per User</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <span className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">ARPPU</span>
            <p className="text-xl font-bold text-[#111614] mt-1">${data.arppu}</p>
            <p className="text-xs text-[#5C6661]">Per Paying User</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4 text-center">
            <span className="text-xs font-medium text-[#5C6661] uppercase tracking-wide">LTV</span>
            <p className="text-xl font-bold text-emerald-600 mt-1">${data.ltv}</p>
            <p className="text-xs text-[#5C6661]">Lifetime Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-500" />
              Monthly Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.revenue_trend && data.revenue_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#5C6661' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#5C6661' }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E6E8E6' }}
                  />
                  <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex items-center justify-center text-sm text-[#5C6661]">
                No revenue data yet. Revenue will appear here once payments are processed.
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Distribution Pie */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-500" />
              User Tier Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tierDistData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={tierDistData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {tierDistData.map((entry, index) => (
                      <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex items-center justify-center text-sm text-[#5C6661]">
                No users yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Tier */}
      {data.revenue_by_tier && data.revenue_by_tier.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenue by Product</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.revenue_by_tier.map((item) => {
                const maxRev = Math.max(...data.revenue_by_tier.map((r) => r.revenue));
                const pct = maxRev > 0 ? (item.revenue / maxRev) * 100 : 0;
                const color = TIER_COLORS[item.tier] || '#6366f1';
                return (
                  <div key={item.tier} className="flex items-center gap-4">
                    <div className="w-28 text-sm font-medium text-[#111614] capitalize">
                      {item.tier === 'business_plus' ? 'Business+' : item.tier}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div
                        className="h-5 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                    <div className="w-24 text-right text-sm font-semibold text-[#111614]">
                      ${item.revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                    <Badge variant="outline" className="text-xs w-16 justify-center">
                      {item.count} txn{item.count !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cohort Analysis */}
      {data.cohort_analysis && data.cohort_analysis.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              Cohort Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cohort-table">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 pr-4 text-[#5C6661] font-medium">Cohort</th>
                  <th className="pb-2 pr-4 text-[#5C6661] font-medium text-center">Signups</th>
                  <th className="pb-2 pr-4 text-[#5C6661] font-medium text-center">Active</th>
                  <th className="pb-2 pr-4 text-[#5C6661] font-medium text-center">Paid</th>
                  <th className="pb-2 pr-4 text-[#5C6661] font-medium text-center">Churned</th>
                  <th className="pb-2 pr-4 text-[#5C6661] font-medium text-center">Retention</th>
                  <th className="pb-2 text-[#5C6661] font-medium text-center">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {data.cohort_analysis.map((cohort) => (
                  <tr key={cohort.month} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2.5 pr-4 font-medium text-[#111614]">{cohort.label}</td>
                    <td className="py-2.5 pr-4 text-center">{cohort.total_signups}</td>
                    <td className="py-2.5 pr-4 text-center text-blue-600 font-medium">{cohort.active}</td>
                    <td className="py-2.5 pr-4 text-center text-emerald-600 font-medium">{cohort.paid}</td>
                    <td className="py-2.5 pr-4 text-center text-red-500">{cohort.churned}</td>
                    <td className="py-2.5 pr-4 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        cohort.retention_rate >= 70 ? 'bg-emerald-100 text-emerald-700' :
                        cohort.retention_rate >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {cohort.retention_rate}%
                      </span>
                    </td>
                    <td className="py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        cohort.conversion_rate >= 10 ? 'bg-emerald-100 text-emerald-700' :
                        cohort.conversion_rate >= 3 ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {cohort.conversion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      {data.recent_transactions && data.recent_transactions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-emerald-500" />
              Recent Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.recent_transactions.map((txn) => (
                <div key={txn.email + txn.date} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#111614]">{txn.email}</p>
                      <p className="text-xs text-[#5C6661]">{txn.plan}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-emerald-600">+${txn.amount.toFixed(2)}</p>
                    <p className="text-xs text-[#5C6661]">
                      {txn.date ? new Date(txn.date).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RevenueDashboard;
